/**
 * French formatting in one place, so a price or a date reads the same on every page.
 * Prices travel as integer cents: floats would turn 19,90 € into 19,899999.
 */

export const formatPrice = (cents: number | null | undefined, currency = "EUR"): string | null => {
  if (cents === null || cents === undefined) return null;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    // Whole prices read cleaner without ",00"; anything else keeps its cents.
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
};

/** "19,90" or "19.90" typed by a person, to cents. Null when it is not a price. */
export const parsePriceInput = (value: string): number | null => {
  const normalized = value.replace(/\s|€/g, "").replace(",", ".");
  if (normalized === "") return null;
  const amount = Number(normalized);
  if (!Number.isFinite(amount) || amount < 0) return null;
  return Math.round(amount * 100);
};

export const priceInputValue = (cents: number | null): string =>
  cents === null ? "" : (cents / 100).toFixed(2).replace(".", ",").replace(/,00$/, "");

/** Event dates are calendar days (`2026-12-25`), never instants: parse them as local. */
const parseDay = (iso: string): Date => {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
};

export const formatDay = (iso: string | null): string | null =>
  iso
    ? new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" }).format(
        parseDay(iso),
      )
    : null;

/**
 * Today in France, as `2026-12-25`. Not the device's own day: the server renders in UTC,
 * and a countdown that reads "J-48" in the HTML and "J-47" once the browser takes over
 * breaks hydration every night between midnight and 2am.
 */
export const todayInFrance = () =>
  // en-CA formats as YYYY-MM-DD.
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris" }).format(new Date());

/**
 * The next 25 December, as `2026-12-25`: today's if it is Christmas today, in France.
 * The default date for anything made for Christmas.
 */
export const nextChristmas = (): string => {
  const today = todayInFrance();
  const year = Number(today.slice(0, 4));
  return today <= `${year}-12-25` ? `${year}-12-25` : `${year + 1}-12-25`;
};

const utcDay = (iso: string) => {
  const [year, month, day] = iso.split("-").map(Number);
  return Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1);
};

/** Whole days from today to the event: 0 is today, negative is past. */
export const daysUntil = (iso: string | null): number | null => {
  if (!iso) return null;
  return Math.round((utcDay(iso) - utcDay(todayInFrance())) / 86_400_000);
};

export const countdownLabel = (iso: string | null): string | null => {
  const days = daysUntil(iso);
  if (days === null) return null;
  if (days < 0) return "C'est passé";
  if (days === 0) return "C'est aujourd'hui";
  if (days === 1) return "C'est demain";
  return `J-${days}`;
};

export const plural = (count: number, one: string, many: string) =>
  `${count} ${count > 1 ? many : one}`;

export const looksLikeUrl = (value: string): boolean => /^https?:\/\/\S+\.\S+/i.test(value.trim());

/** "il y a 3 jours", "le mois dernier": how long ago, in words. */
export const relativeDate = (iso: string): string => {
  const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["week", 604_800],
    ["day", 86_400],
    ["hour", 3600],
    ["minute", 60],
  ];
  const format = new Intl.RelativeTimeFormat("fr-FR", { numeric: "auto" });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return "à l'instant";
};
